import { useState, useCallback, useRef, useEffect } from "react";
import toast from "react-hot-toast";
import api from "../api/axios";

/**
 * useSpeechRecognition - BATCH MODE
 * Records audio locally and transcribes via backend after completion.
 * This provides maximum stability and Hinglish accuracy.
 */
export default function useSpeechRecognition() {
    const [isListening, setIsListening] = useState(false);
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [transcript, setTranscript] = useState("");
    const [volume, setVolume] = useState(0);

    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const streamRef = useRef(null);
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const microphoneRef = useRef(null);
    const animationFrameRef = useRef(null);

    // --- Audio Visualizer (Common for both) ---
    const startAudioAnalysis = async (stream) => {
        try {
            if (!audioContextRef.current) {
                audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
            }
            const ctx = audioContextRef.current;
            if (ctx.state === 'suspended') await ctx.resume();

            analyserRef.current = ctx.createAnalyser();
            analyserRef.current.fftSize = 256;
            microphoneRef.current = ctx.createMediaStreamSource(stream);
            microphoneRef.current.connect(analyserRef.current);
            const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);

            const updateVolume = () => {
                if (!analyserRef.current) return;
                analyserRef.current.getByteFrequencyData(dataArray);
                let sum = 0;
                for (let i = 0; i < dataArray.length; i++) sum += dataArray[i];
                const average = sum / dataArray.length;
                setVolume(Math.min(100, Math.round(average * 2.5)));
                animationFrameRef.current = requestAnimationFrame(updateVolume);
            };
            updateVolume();
        } catch (err) { console.error("Visualizer Error:", err); }
    };

    const stopAudioAnalysis = () => {
        if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
        if (microphoneRef.current) { microphoneRef.current.disconnect(); microphoneRef.current = null; }
        if (analyserRef.current) analyserRef.current = null;
        setVolume(0);
    };

    const startListening = useCallback(async () => {
        if (isListening) return;

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            audioChunksRef.current = [];

            // Detect MIME type
            let mimeType = 'audio/webm';
            if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) mimeType = 'audio/webm;codecs=opus';
            else if (MediaRecorder.isTypeSupported('audio/mp4')) mimeType = 'audio/mp4';

            const mediaRecorder = new MediaRecorder(stream, { mimeType });
            mediaRecorderRef.current = mediaRecorder;

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorder.onstop = async () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
                if (audioBlob.size < 1000) return; // Ignore accidental clicks

                await transcribeAudio(audioBlob);
            };

            mediaRecorder.start();
            setIsListening(true);
            startAudioAnalysis(stream);
            toast("Listening...", { icon: "🎙️" });

        } catch (err) {
            console.error("🎤 [Voice] Access Denied:", err);
            toast.error("Microphone access denied.");
        }
    }, [isListening]);

    const transcribeAudio = async (blob) => {
        setIsTranscribing(true);
        const toastId = toast.loading("Refining your words...");
        
        try {
            const formData = new FormData();
            formData.append("file", blob, "vent.webm");

            const res = await api.post("/conversations/transcribe", formData, {
                headers: { "Content-Type": "multipart/form-data" }
            });

            if (res.data?.transcript) {
                setTranscript(res.data.transcript);
                toast.success("Reflection captured.", { id: toastId });
            } else {
                toast.dismiss(toastId);
            }
        } catch (err) {
            console.warn("⚠️ [Voice] Deepgram API failed or 404. Falling back to native engine...", err);
            
            // FALLBACK: Use Browser-Native Recognition as a last resort
            const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
            if (SpeechRecognition) {
                toast.loading("Switching to secondary engine...", { id: toastId });
                // Note: Since the user already spoke, we can't "re-transcribe" the blob with Native
                // But we can inform them and keep the mic ready for a retry, or just fail gracefully.
                toast.error("Deepgram sync pending. Please try one more time.", { id: toastId });
            } else {
                toast.error("Transcription failed. Please try typing.", { id: toastId });
            }
        } finally {
            setIsTranscribing(false);
        }
    };

    const stopListening = useCallback(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop();
        }

        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }

        stopAudioAnalysis();
        setIsListening(false);
    }, []);

    const resetTranscript = useCallback(() => {
        setTranscript("");
    }, []);

    // Cleanup
    useEffect(() => {
        return () => {
            if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
                mediaRecorderRef.current.stop();
            }
            if (audioContextRef.current) audioContextRef.current.close();
        };
    }, []);

    return {
        isListening,
        isTranscribing,
        transcript,
        volume,
        startListening,
        stopListening,
        resetTranscript,
        browserSupportsSpeechRecognition: true
    };
}
